import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { en, lv, ru, Language, defaultLanguage, TranslationKeys } from '@/i18n';

const translations = { en, lv, ru };

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationKeys;
  getLocalizedPath: (path: string) => string;
  getPathWithoutLang: (path: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Extract language from path
const getLanguageFromPath = (pathname: string): Language | null => {
  const match = pathname.match(/^\/(en|lv|ru)(\/|$)/);
  return match ? (match[1] as Language) : null;
};

// Remove language prefix from path
const stripLanguageFromPath = (pathname: string): string => {
  return pathname.replace(/^\/(en|lv|ru)(\/|$)/, '/');
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get initial language from URL or localStorage or default
  const getInitialLanguage = (): Language => {
    const pathLang = getLanguageFromPath(location.pathname);
    if (pathLang) return pathLang;
    
    const stored = localStorage.getItem('language') as Language;
    if (stored && ['en', 'lv', 'ru'].includes(stored)) return stored;
    
    // Check browser language
    const browserLang = navigator.language.split('-')[0];
    if (['en', 'lv', 'ru'].includes(browserLang)) return browserLang as Language;
    
    return defaultLanguage;
  };

  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  // Update language when URL changes
  useEffect(() => {
    const pathLang = getLanguageFromPath(location.pathname);
    if (pathLang && pathLang !== language) {
      setLanguageState(pathLang);
      localStorage.setItem('language', pathLang);
    }
  }, [location.pathname]);

  const setLanguage = useCallback((lang: Language) => {
    if (lang === language) return;
    
    localStorage.setItem('language', lang);
    setLanguageState(lang);
    
    // Navigate to the same page with new language prefix
    const currentPath = stripLanguageFromPath(location.pathname);
    const newPath = `/${lang}${currentPath === '/' ? '' : currentPath}${location.search}`;
    navigate(newPath, { replace: true });
  }, [language, location.pathname, location.search, navigate]);

  const getLocalizedPath = useCallback((path: string): string => {
    const cleanPath = stripLanguageFromPath(path);
    return `/${language}${cleanPath === '/' ? '' : cleanPath}`;
  }, [language]);

  const getPathWithoutLang = useCallback((path: string): string => {
    return stripLanguageFromPath(path);
  }, []);

  const t = translations[language];

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, getLocalizedPath, getPathWithoutLang }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    // Fallback for cases when context is not yet available (e.g., during hot reload)
    console.warn('useLanguage called outside LanguageProvider, using fallback');
    return {
      language: 'lv',
      setLanguage: () => {},
      t: translations.lv,
      getLocalizedPath: (path: string) => `/lv${path === '/' ? '' : path}`,
      getPathWithoutLang: (path: string) => path.replace(/^\/(en|lv|ru)(\/|$)/, '/'),
    };
  }
  return context;
};

// HOC for components that need language without full context
export const useTranslation = () => {
  const { t, language } = useLanguage();
  return { t, language };
};
